import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { caretAfterDigits, digitsBefore } from '@/lib';

/**
 * Click-to-edit cell: a borderless input with a colored bottom border while
 * editing (the design's inline pattern). The buffer is local — the document
 * only changes on commit (Enter / blur); Escape cancels.
 */
/**
 * How a commit was triggered: plain (Enter/blur), Tab field-cycling, or the
 * ⌘Return outline-entry family (new sibling / sub-task / one level up).
 */
export type CommitIntent =
  | 'commit'
  | 'tab-next'
  | 'tab-prev'
  | 'new-sibling'
  | 'new-child'
  | 'new-parent-sibling'
  /** Plain Enter with `advanceOnEnter` — commit AND move to the next field (v8 Financials). */
  | 'enter-next';

export type InlineTextCellProps = {
  value: string;
  display: ReactNode;
  editing: boolean;
  onStartEdit: () => void;
  onCommit: (value: string, intent: CommitIntent) => void;
  onCancel: () => void;
  /**
   * Plain Enter commits as 'enter-next' instead of 'commit' so the caller can
   * advance focus (the Financials keyboard path). Blur still commits plain —
   * a click elsewhere must never move the editing focus.
   */
  advanceOnEnter?: boolean;
  placeholder?: string;
  align?: 'left' | 'right';
  underlineColor?: string;
  inputWidth?: number | string;
  displaySx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  /**
   * Rewrites the field's text on every keystroke — digit grouping on amount
   * fields, so a million is distinguishable from a thousand while it is being
   * typed rather than counted afterwards. The caret is kept over the same
   * DIGIT, not the same character offset.
   */
  formatWhileTyping?: (raw: string) => string;
  automationId?: string;
};

type EditorInputProps = Omit<
  InlineTextCellProps,
  'display' | 'editing' | 'onStartEdit' | 'displaySx'
>;

/** Mounted fresh each time editing starts, so the buffer seeds at mount. */
const EditorInput = ({
  value,
  onCommit,
  onCancel,
  placeholder,
  align,
  underlineColor,
  inputWidth,
  inputSx,
  automationId,
  advanceOnEnter,
  formatWhileTyping,
}: EditorInputProps) => {
  const [buffer, setBuffer] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  /*
    Once-only per edit, so Enter and the blur that follows unmount do not
    commit twice. Re-armed on focus and on change: a parent may keep the
    editor mounted after a commit it ignored (the Financials guided walk keeps
    its step's field open on an empty/unparsable value), and without the
    re-arm every later Enter on that field was swallowed.
  */
  const committedRef = useRef(false);
  /*
    Where the caret belongs once a reformat has moved the characters under it,
    in DIGITS rather than offsets. Restored in a layout effect because the input
    is controlled: React writes the new value on re-render, and any selection
    set before that is overwritten.
  */
  const caretDigits = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (caretDigits.current === null) return;
    const input = inputRef.current;
    const position = input === null ? 0 : caretAfterDigits(input.value, caretDigits.current);
    caretDigits.current = null;
    input?.setSelectionRange(position, position);
  });

  useEffect(() => {
    // Focus after mount; select for fast overwrite.
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  const commit = (intent: CommitIntent = 'commit') => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(buffer, intent);
  };

  return (
    <Box
      component="input"
      ref={inputRef}
      data-automation-id={automationId ? `${automationId}-input` : undefined}
      value={buffer}
      placeholder={placeholder}
      onClick={(event) => event.stopPropagation()}
      onFocus={() => {
        committedRef.current = false;
      }}
      onChange={(event) => {
        committedRef.current = false;
        const input = event.target as HTMLInputElement;
        if (!formatWhileTyping) {
          setBuffer(input.value);
          return;
        }
        caretDigits.current = digitsBefore(input.value, input.selectionStart ?? input.value.length);
        setBuffer(formatWhileTyping(input.value));
      }}
      onBlur={() => commit()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          const mod = event.metaKey || event.ctrlKey;
          if (mod && event.shiftKey) commit('new-child');
          else if (mod && event.altKey) commit('new-parent-sibling');
          else if (mod) commit('new-sibling');
          else commit(advanceOnEnter ? 'enter-next' : 'commit');
        } else if (event.key === 'Tab') {
          event.preventDefault();
          commit(event.shiftKey ? 'tab-prev' : 'tab-next');
        } else if (event.key === 'Escape') {
          event.preventDefault();
          committedRef.current = true;
          onCancel();
        }
      }}
      sx={{
        fontFamily: 'var(--font-family)',
        fontSize: 14,
        color: 'var(--fg-1)',
        background: 'transparent',
        border: 'none',
        borderBottom: `1.5px solid ${underlineColor}`,
        outline: 'none',
        p: '0 1px',
        width: inputWidth,
        minWidth: 0,
        textAlign: align,
        ...inputSx,
      }}
    />
  );
};

export const InlineTextCell = ({
  value,
  display,
  editing,
  onStartEdit,
  onCommit,
  onCancel,
  placeholder,
  align = 'left',
  underlineColor = 'var(--color-blue-80)',
  inputWidth = '100%',
  displaySx,
  inputSx,
  automationId,
  advanceOnEnter,
  formatWhileTyping,
}: InlineTextCellProps) => {
  if (!editing) {
    return (
      <Box
        component="span"
        data-automation-id={automationId}
        onClick={(event) => {
          event.stopPropagation();
          onStartEdit();
        }}
        sx={{
          cursor: 'text',
          px: 0.5,
          mx: -0.5,
          py: 0.5,
          borderRadius: 'var(--border-radius-small)',
          '&:hover': { bgcolor: 'var(--bg-hover)' },
          ...displaySx,
        }}
      >
        {display}
      </Box>
    );
  }

  return (
    <EditorInput
      value={value}
      onCommit={onCommit}
      onCancel={onCancel}
      placeholder={placeholder}
      align={align}
      underlineColor={underlineColor}
      inputWidth={inputWidth}
      inputSx={inputSx}
      automationId={automationId}
      advanceOnEnter={advanceOnEnter}
      formatWhileTyping={formatWhileTyping}
    />
  );
};
